import { isUuid } from '@leanin/shared';
import type { GroupKind } from '@/lib/group-url';
import { readStorage, removeStorage, writeStorage } from '@/lib/browser-storage';

// Group destination carried across onboarding for join-link signups.
// Written at /auth/finishing when the attribution claim reports the signup
// arrived through a group join link; read once by the onboarding completion
// step to land the new member on the advertised group instead of the
// generic /feed. Cleared on EVERY onboarding finish (not just when read on
// the happy path) so a stale value can't leak into a later signup in the
// same tab — the pendingInviteToken equivalent of that leak already bit us.
const POST_ONBOARDING_GROUP_KEY = 'onboarding.postOnboardingGroup';

export interface PostOnboardingGroup {
  readonly kind: GroupKind;
  readonly id: string;
  // The post-signup auto-join was refused with `groups/intake_required`, so
  // the completion step must land the member in the group's Request-to-Join
  // modal (`?join=1`) instead of assuming a pending request already exists.
  readonly needsJoin?: true;
  // The group came from a "Sign up to Join" click on its own page (the
  // pendingJoinGroup intent) rather than a join-link claim. That concrete
  // intent outranks the journey's directory landing at the completion step —
  // the member asked for this group, not for a browse surface.
  readonly fromIntent?: true;
}

const GROUP_KINDS: ReadonlyArray<GroupKind> = ['circle', 'network', 'space'];

export function writePostOnboardingGroup(group: PostOnboardingGroup): void {
  if (typeof window === 'undefined') return;
  writeStorage('session', POST_ONBOARDING_GROUP_KEY, JSON.stringify(group));
}

export function readPostOnboardingGroup(): PostOnboardingGroup | null {
  if (typeof window === 'undefined') return null;
  const raw = readStorage('session', POST_ONBOARDING_GROUP_KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    if (!(err instanceof SyntaxError)) throw err;
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const candidate = parsed as { kind?: unknown; id?: unknown };
  const withFlag = parsed as { needsJoin?: unknown; fromIntent?: unknown };
  if (
    typeof candidate.kind === 'string' &&
    (GROUP_KINDS as ReadonlyArray<string>).includes(candidate.kind) &&
    typeof candidate.id === 'string' &&
    isUuid(candidate.id)
  ) {
    return {
      kind: candidate.kind as GroupKind,
      id: candidate.id,
      ...(withFlag.needsJoin === true ? { needsJoin: true as const } : {}),
      ...(withFlag.fromIntent === true ? { fromIntent: true as const } : {}),
    };
  }
  return null;
}

export function clearPostOnboardingGroup(): void {
  if (typeof window === 'undefined') return;
  removeStorage('session', POST_ONBOARDING_GROUP_KEY);
}
